import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { useAuth } from "@/hooks/useAuth"

export interface EmployeeHoursBank {
  id: string
  employee_id: string
  condominium_id: string
  total_hours_worked: number
  required_hours: number
  balance_hours: number
  created_at: string
  updated_at: string
}

export interface HoursCalculation {
  total_worked: number
  required_hours: number
  balance: number
}

export const useEmployeeHoursBank = (employeeId?: string) => {
  const { profile } = useAuth()
  
  return useQuery({
    queryKey: ["employee-hours-bank", employeeId || profile?.id],
    queryFn: async (): Promise<EmployeeHoursBank | null> => {
      const targetEmployeeId = employeeId || profile?.id
      
      if (!targetEmployeeId) {
        throw new Error("Employee ID não encontrado")
      }

      const { data, error } = await supabase
        .from("employee_hours_bank")
        .select("*")
        .eq("employee_id", targetEmployeeId)
        .maybeSingle()

      if (error) throw error
      return data
    },
    enabled: !!(employeeId || profile?.id)
  })
}

export const useCalculateEmployeeHours = (employeeId?: string, condominiumId?: string) => {
  const { profile } = useAuth()
  
  return useQuery({
    queryKey: ["calculate-employee-hours", employeeId || profile?.id, condominiumId || profile?.condominium_id],
    queryFn: async (): Promise<HoursCalculation> => {
      const targetEmployeeId = employeeId || profile?.id
      const targetCondominiumId = condominiumId || profile?.condominium_id
      
      if (!targetEmployeeId || !targetCondominiumId) {
        throw new Error("Employee ID ou Condominium ID não encontrado")
      }

      // SEGURANÇA: Verificar se o usuário tem permissão (não é superadmin)
      if (profile?.role !== 'superadmin' && employeeId && employeeId !== profile?.id) {
        // Buscar perfil do funcionário para comparar condomínio
        const { data: employeeProfile, error: profileError } = await supabase
          .from('profiles')
          .select('condominium_id')
          .eq('id', targetEmployeeId)
          .single()
        
        if (profileError) {
          console.error("Erro ao buscar perfil do funcionário:", profileError)
          throw profileError
        }
        
        // Verificar se o funcionário pertence ao mesmo condomínio
        if (employeeProfile?.condominium_id !== profile?.condominium_id) {
          const error = new Error('Acesso negado: você não tem permissão para ver dados de funcionários de outro condomínio')
          console.error(error.message)
          throw error
        }
      }

      const { data, error } = await supabase
        .rpc("calculate_employee_hours_bank", {
          employee_uuid: targetEmployeeId,
          condominium_uuid: targetCondominiumId
        })

      if (error) throw error
      
      return data?.[0] || { total_worked: 0, required_hours: 0, balance: 0 }
    },
    enabled: !!(employeeId || profile?.id) && !!(condominiumId || profile?.condominium_id)
  })
}

export const useUpdateEmployeeHoursBank = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      employee_id: string
      condominium_id: string
      total_hours_worked: number
      required_hours: number
    }) => {
      const { data: result, error } = await supabase
        .from("employee_hours_bank")
        .upsert({
          employee_id: data.employee_id,
          condominium_id: data.condominium_id,
          total_hours_worked: data.total_hours_worked,
          required_hours: data.required_hours
        })
        .select()
        .single()

      if (error) throw error
      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employee-hours-bank"] })
      queryClient.invalidateQueries({ queryKey: ["calculate-employee-hours"] })
      toast.success("Banco de horas atualizado com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao atualizar banco de horas:", error)
      toast.error("Erro ao atualizar banco de horas")
    }
  })
}

export const useEmployeesHoursBank = (condominiumId?: string) => {
  const { profile } = useAuth()
  
  return useQuery({
    queryKey: ["employees-hours-bank", condominiumId || profile?.condominium_id],
    queryFn: async (): Promise<(EmployeeHoursBank & { employee: { full_name: string; email: string } })[]> => {
      const targetCondominiumId = condominiumId || profile?.condominium_id
      
      if (!targetCondominiumId) {
        throw new Error("Condominium ID não encontrado")
      }

      const { data, error } = await supabase
        .from("employee_hours_bank")
        .select(`
          *,
          employee:profiles!employee_id (
            full_name,
            email
          )
        `)
        .eq("condominium_id", targetCondominiumId)
        .order("created_at", { ascending: false })

      if (error) throw error
      return data || []
    },
    enabled: !!(condominiumId || profile?.condominium_id)
  })
}